/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  HostListener,
  ViewChild,
  ViewChildren,
  QueryList,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import {
  trigger,
  style,
  animate,
  transition,
  sequence,
} from '@angular/animations';
import {
  WorkflowService,
  Workflow,
  FileType,
  Service,
} from '../services/workflow.service';
import {
  AutomatedWorkflowService,
  AutomatedWorkflow,
  AutomatedJobHistory,
} from '../services/automated-workflow.service';
import { XdaStringsService } from '../services/xda-strings.service';
import { BasicAlertComponent } from './alerts/basic-alert.component';
import {
  NgbModal,
  NgbModalRef,
  NgbNavChangeEvent,
  NgbTooltipConfig,
} from '@ng-bootstrap/ng-bootstrap';
import {
  WorkflowModalComponent,
  WorkflowModalResult,
} from './modals/workflow-modal.component';
import {
  ProductPlanTier,
  UserService,
  AccessListRole,
} from '../services/user.service';
import { WorkflowHelperService } from '../services/workflow-helper.service';
import {
  AccessListConfig,
  AccessListService,
} from '../services/access-list.service';
import { Subscription } from 'rxjs';
import { FileService } from '../services/file.service';
import { ConfigureServicesFeaturesComponent } from './configureServicesFeatures/configure-services-features.component';
import { ActivityIndicatorModalComponent } from '../components/modals/activity-indicator-modal.component';
import { XrxJobHistoryModalComponent } from './automatedWorkflows/xrx-job-history-modal.component';
import { AutomatedWorkflowModalComponent } from './modals/automated-workflow-modal.component';
import { XaccService, MWCloudRepo } from '../xacc/xacc.service';
import { SqdStatusState } from '../services/workflow-status.service';
import { Constants } from '../../common/constants';
import { MergeMode, SettingType } from '../../common/enums';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ActiveDocType } from '../services/active-documents.service';
import { pageSizeType } from './controls/xrx-pagination.component';

@Component({
  selector: 'app-workflows',
  templateUrl: './workflows.component.html',
  styleUrls: ['./workflows.component.less'],
  animations: [
    trigger('displayAnimatedMessage', [
      transition(':enter', [
        style({ opacity: 0, height: '0px' }),
        sequence([
          animate(
            500,
            style({
              height: '*',
            }),
          ),
          animate(
            500,
            style({
              opacity: 1,
            }),
          ),
        ]),
      ]),
      transition(':leave', [
        sequence([
          animate(
            500,
            style({
              opacity: 0,
            }),
          ),
          animate(
            500,
            style({
              height: '0px',
            }),
          ),
        ]),
      ]),
    ]),
    trigger('deleteSavedWorkflowButton', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate(
          '500ms 500ms',
          style({
            opacity: 1,
          }),
        ),
      ]),
      transition(':leave', [
        sequence([
          animate(
            500,
            style({
              opacity: 0,
            }),
          ),
        ]),
      ]),
    ]),
  ],
  standalone: false,
})
export class WorkflowsComponent implements AfterViewInit, OnDestroy {
  preconfiguredWorkflows: Workflow[] = [];
  singleWorkflows: Workflow[] = [];
  combinedWorkflows: Workflow[] = [];
  savedWorkflows: Workflow[] = [];
  automatedWorkflows: AutomatedWorkflow[] = [];
  toggleDeleteWorkflow = false;
  accessListDiscoveredSub: Subscription;
  xaccServiceLoginSub: Subscription;
  canBeDeleted = false;
  isAdvancedPlan = false;
  hideMergeWorkflows = false;
  accessListId = '';
  configureEnabled = false;
  storedConfig: AccessListConfig;
  modalActivityRef: NgbModalRef;
  modalActivityRefCount = 0;
  cloudRepos = Constants.middlewareDestinations;
  configUpdated = false;
  activeTab = 'SDE_SINGLE_WORKFLOWS';
  isGuest = false;
  configIsExpanded = false;
  direction = 'asc';
  column = '';
  type = 'string';
  page = 1;
  initialPageSize = 20;
  pageSize = 20;
  pageSizeList: pageSizeType[] = [
    { size: 10, key: '', value: '' },
    { size: 20, key: '', value: '' },
    { size: 50, key: '', value: '' },
  ];
  displayPagination = true;
  tableFilterMetadata = { count: 0 };
  searchTerm = '';
  searchMinLength = '3';
  searchPlaceholder: string;
  clearTooltip: string;
  showClearSearch = false;
  searchIsValid = true;
  wfTooltips: NgbTooltip[] = [];
  isAutoWorkflowsDisabled = false;
  toggleRunningWorkflow = false;
  runningWorkflowName = '';
  isSticky = false;
  isLoadingWorkflows = true;
  isLoadingAutoWorkflows = true;

  @ViewChild('configDropdown') configDropdown: {
    nativeElement: { contains: (arg0: unknown) => unknown };
  };
  @ViewChildren('workflowTooltip') wfTooltipsQL: QueryList<NgbTooltip>;

  constructor(
    public workflowService: WorkflowService,
    public automatedWorkflowService: AutomatedWorkflowService,
    private workflowHelper: WorkflowHelperService,
    private modalService: NgbModal,
    private userService: UserService,
    private tooltipConfig: NgbTooltipConfig,
    private accessListService: AccessListService,
    private xdaStringsService: XdaStringsService,
    private fileService: FileService,
    public xaccService: XaccService,
  ) {
    this.accessListId = this.userService.user?.accessListId || '';
    this.tooltipConfig.tooltipClass = 'xrx-tooltip';
    this.getWorkflows(this.fileService.hasUploadedFileId).then(() => {
      this.getAutoWorkflows(this.activeTab);
    });
    this.hideMergeWorkflows = this.fileService.hasUploadedFileId;
    this.configureEnabled =
      this.userService.user?.accessListRole === AccessListRole.Owner ||
      this.userService.user?.accessListRole === AccessListRole.Manager;
    this.isAdvancedPlan =
      (this.userService.user?.acquisition?.productPlanTier &&
        this.userService.user?.acquisition?.productPlanTier >=
          ProductPlanTier.Advanced) ||
      false;
    this.accessListDiscoveredSub = this.userService.discovered.subscribe(() => {
      this.configureEnabled =
        this.userService.user?.accessListRole === AccessListRole.Owner ||
        this.userService.user?.accessListRole === AccessListRole.Manager;
      this.accessListId = this.userService.user?.accessListId || '';
      this.getWorkflows(this.fileService.hasUploadedFileId).then(() => {
        this.getAutoWorkflows(this.activeTab);
      });
    });
    this.searchPlaceholder = this.xdaStringsService.strings['SDE_SEARCH'];
    this.clearTooltip = this.xdaStringsService.strings['SDE_CLEAR_SEARCH'];
  }

  ngAfterViewInit() {
    this.wfTooltips = this.wfTooltipsQL.toArray();
    this.wfTooltipsQL.changes.subscribe(() => {
      this.wfTooltips = this.wfTooltipsQL.toArray();
    });
  }

  showActivityIndicator(show = true) {
    if (show) {
      if (this.modalActivityRefCount++ === 0) {
        this.modalActivityRef = this.modalService.open(
          ActivityIndicatorModalComponent,
          {
            windowClass: 'xrx-modal transparent',
            centered: true,
            backdrop: 'static',
          },
        );
      }
    } else if (--this.modalActivityRefCount === 0) {
      this.modalActivityRef.close();
    }
  }

  async getWorkflows(hideMergeWorkflows = false): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isGuest = this.userService.isGuestUser;
      if (!this.userService.user?.isSubscriptionExpired) {
        this.isLoadingWorkflows = true;
        this.showActivityIndicator();

        this.configRefresh().then(
          () => {
            this.workflowService.getWorkflows()?.subscribe(
              (resp) => {
                // deal with existing workflows that use .png icons instead of .svg icons
                this.workflowService.workflows = resp.map((w) => {
                  if (w.icon.image) {
                    return {
                      ...w,
                      icon: {
                        ...w.icon,
                        image: w.icon.image.replace('.png', '.svg'),
                      },
                    };
                  } else {
                    return w;
                  }
                });
                this.combinedWorkflows = [];
                this.workflowService.workflows.forEach((w) => {
                  if (this.storedConfig) {
                    if (
                      !w.isSingleService &&
                      w.type === 0 &&
                      !(
                        hideMergeWorkflows &&
                        w.input.maxConcatenatedDocument >= 1
                      ) &&
                      !this.storedConfig.hiddenWorkflows.includes(w.id || '')
                    ) {
                      this.combinedWorkflows.push(w);
                    }
                  } else {
                    if (
                      !w.isSingleService &&
                      w.type === 0 &&
                      !(
                        hideMergeWorkflows &&
                        w.input.maxConcatenatedDocument >= 1
                      )
                    ) {
                      this.combinedWorkflows.push(w);
                    }
                  }
                  w.isEncryptAndTrackDisabled =
                    this.storedConfig &&
                    this.storedConfig.disableEncryptedEmail;
                });
                this.combinedWorkflows.sort((a, b) =>
                  a.name.localeCompare(b.name),
                );

                if (this.storedConfig) {
                  this.preconfiguredWorkflows = this.workflowService.workflows
                    .filter(
                      (x) =>
                        x.type == 0 &&
                        x.isSingleService &&
                        !(
                          hideMergeWorkflows &&
                          x.input.maxConcatenatedDocument >= 1
                        ) &&
                        !this.storedConfig.hiddenWorkflows.includes(x.id || ''),
                    )
                    .sort((a, b) => {
                      const aTranslated =
                        this.xdaStringsService.strings[a.name] || a.name;
                      const bTranslated =
                        this.xdaStringsService.strings[b.name] || b.name;
                      return aTranslated.localeCompare(bTranslated);
                    });
                } else {
                  this.preconfiguredWorkflows = this.workflowService.workflows
                    .filter(
                      (x) =>
                        x.type == 0 &&
                        x.isSingleService &&
                        !(
                          hideMergeWorkflows &&
                          x.input.maxConcatenatedDocument >= 1
                        ),
                    )
                    .sort((a, b) => {
                      const aTranslated =
                        this.xdaStringsService.strings[a.name] || a.name;
                      const bTranslated =
                        this.xdaStringsService.strings[b.name] || b.name;
                      return aTranslated.localeCompare(bTranslated);
                    });
                }

                this.singleWorkflows = this.preconfiguredWorkflows.filter(
                  (x) => x.name !== 'SDE_CATEGORIZE',
                ); // hide Categorize from single workflows
                this.savedWorkflows = this.workflowService.workflows
                  .filter(
                    (x) =>
                      x.type == 1 &&
                      !(
                        hideMergeWorkflows &&
                        x.input.maxConcatenatedDocument >= 1
                      ),
                  )
                  .sort((a, b) => a.name.localeCompare(b.name));

                const userid = this.userService.user?.id || '';
                this.canBeDeleted = false;
                this.savedWorkflows.forEach((workflow) => {
                  workflow.isShared = workflow.owner != userid;
                  workflow.canBeDeleted =
                    workflow.creator == userid ||
                    (this.userService.user?.accessListRole != 'User' &&
                      workflow.isOrphan);
                  if (workflow.canBeDeleted) {
                    this.canBeDeleted = true;
                  }
                  workflow.isDisabled = Boolean(
                    (this.storedConfig &&
                      this.storedConfig.disableEncryptedEmail &&
                      workflow.output.sendEncryptedEmail) ||
                    (this.storedConfig &&
                      workflow.parentId &&
                      this.storedConfig.hiddenWorkflows.includes(
                        workflow.parentId,
                      )) ||
                    (this.storedConfig &&
                      this.storedConfig.disableCloudConnectors.includes(
                        (workflow.input.cloudRepoOptions?.connector || '') +
                          (workflow.input.cloudRepoOptions?.region || ''),
                      )) ||
                    (this.storedConfig &&
                      this.storedConfig.disableCloudConnectors.includes(
                        (workflow.output.cloudRepoOptions?.connector || '') +
                          (workflow.output.cloudRepoOptions?.region || ''),
                      )) ||
                    (this.storedConfig &&
                      this.storedConfig.disableCloudConnectors.length ===
                        Constants.middlewareDestinations.length &&
                      workflow.output.outputOptions?.some(
                        (o) => o.value === 'Cloud',
                      )),
                  );

                  workflow.isHidden =
                    workflow.isShared &&
                    workflow.isDisabled &&
                    !workflow.canBeDeleted;
                });

                if (this.savedWorkflowsAllHidden()) {
                  this.toggleDeleteWorkflow = false;
                }

                this.showActivityIndicator(false);
                this.isLoadingWorkflows = false;
                resolve();
              },
              () => {
                this.showActivityIndicator(false);
                this.isLoadingWorkflows = false;
                const modalRef = this.modalService.open(BasicAlertComponent);
                modalRef.componentInstance.title =
                  'SDE_ERROR_GETTING_WORKFLOWS';
                reject();
              },
            );
          },
          () => {
            this.showActivityIndicator(false);
            this.isLoadingWorkflows = false;
            reject();
          },
        );
      } else {
        this.isLoadingWorkflows = false;
        reject();
      }
    });
  }
  getAutoWorkflows(tab: string) {
    if (!this.userService.user?.isSubscriptionExpired) {
      this.isLoadingAutoWorkflows = true;
      this.showActivityIndicator();
      this.configRefresh().then(
        () => {
          this.automatedWorkflowService
            .getWorkflows(tab === 'SDE_AUTOMATED_WORKFLOWS')
            ?.subscribe(
              // get job counts if the current tab is automated workflows
              (resp) => {
                // deal with existing workflows that use .png icons instead of .svg icons
                this.automatedWorkflowService.workflows = resp.map((w) => {
                  if (w.icon?.image) {
                    return {
                      ...w,
                      icon: {
                        ...w.icon,
                        image: w.icon.image.replace('.png', '.svg'),
                      },
                    };
                  } else {
                    return w;
                  }
                });
                this.automatedWorkflows = [];

                this.isAutoWorkflowsDisabled ||=
                  this.preconfiguredWorkflows?.length === 0;

                this.automatedWorkflowService.workflows.forEach((workflow) => {
                  const w = JSON.parse(JSON.stringify(workflow));
                  w.jobStatus = {
                    completed: workflow.succeededJobs,
                    failed: workflow.failedJobs,
                  };
                  w.jobs = workflow.succeededJobs + workflow.failedJobs; // for sortable column
                  w.wfName = workflow.name; // need a unique property name for search

                  w.isDisabled =
                    this.isAutoWorkflowsDisabled ||
                    (this.storedConfig &&
                      workflow.parentId &&
                      this.storedConfig.hiddenWorkflows.includes(
                        workflow.parentId,
                      )) ||
                    (this.storedConfig &&
                      this.storedConfig.disableCloudConnectors.includes(
                        (workflow.input.cloudRepoOptions?.connector || '') +
                          (workflow.input.cloudRepoOptions?.region || ''),
                      )) ||
                    (this.storedConfig &&
                      this.storedConfig.disableCloudConnectors.includes(
                        (workflow.output.cloudRepoOptions?.connector || '') +
                          (workflow.output.cloudRepoOptions?.region || ''),
                      )) ||
                    (this.storedConfig &&
                      this.storedConfig.disableCloudConnectors.length ===
                        Constants.middlewareDestinations.length &&
                      workflow.output.outputOptions?.some(
                        (o) => o.value === 'Cloud',
                      ));

                  this.automatedWorkflows.push(w);
                });

                this.displayPagination =
                  this.automatedWorkflows.length <= this.pageSize
                    ? false
                    : true;

                // if autoworkflows are disabled and no autoworkflows the tab will be hidden, make single workflows the active tab.
                if (
                  this.activeTab === 'SDE_AUTOMATED_WORKFLOWS' &&
                  this.isAutoWorkflowsDisabled &&
                  !this.automatedWorkflows.length
                ) {
                  this.activeTab = 'SDE_SINGLE_WORKFLOWS';
                }

                this.showActivityIndicator(false);
                this.isLoadingAutoWorkflows = false;
              },
              () => {
                this.showActivityIndicator(false);
                this.isLoadingAutoWorkflows = false;
                const modalRef = this.modalService.open(BasicAlertComponent);
                modalRef.componentInstance.title =
                  'SDE_ERROR_GETTING_WORKFLOWS';
              },
            );
        },
        () => {
          this.showActivityIndicator(false);
          this.isLoadingAutoWorkflows = false;
        },
      );
    }
  }

  async openWorkflow(workflow: Workflow) {
    if (!workflow.isDisabled) {
      await this.accessListService.refreshAccessConfigConfig(
        this.userService.user?.accessListId,
      ); // refresh stale config data

      const modalRef = this.modalService.open(WorkflowModalComponent, {
        windowClass: 'xrx-modal',
        backdrop: 'static',
      });
      if (this.isAdvancedPlan) {
        modalRef.componentInstance.cloudRepos = this.cloudRepos;
      }
      modalRef.componentInstance.workflow = workflow;

      // If workflows creator is you, let it know
      modalRef.componentInstance.canEditWorkflow =
        workflow.creator == this.userService.user?.id ||
        (this.userService.user?.accessListRole != 'User' && workflow.isOrphan);
      // When a user deletes a workflow refresh workflows
      modalRef.componentInstance.update.subscribe(() => {
        this.getWorkflows();
        this.workflowService.loadSqdServices();
      });
      // When a user selects a workflow, execute it
      modalRef.result.then(
        (workflowData: WorkflowModalResult) => {
          this.toggleDeleteWorkflow = false;
          const workflowsToExecute: WorkflowModalResult[] = [];

          const isNonMergeWorkflow =
            workflowData.workflow.input.mergeMode === MergeMode.None;
          // batch files from cloud repo
          if (
            isNonMergeWorkflow &&
            workflowData.cloudRepoFiles &&
            workflowData.cloudRepoFiles.length > 1
          ) {
            workflowData.cloudRepoFiles.forEach((file) => {
              const wData = JSON.parse(JSON.stringify(workflowData));
              wData.cloudRepoFiles = [file];
              wData.workflow.output.file.name = file.outputFileName;
              wData.workflow.output.file.appendDateTimeToFilename = true;
              if (wData.translateOutputFormat && file.file) {
                wData.workflow.output.file.extension =
                  wData.translateOutputFormat === 'pdf'
                    ? FileType['pdf']
                    : FileType[
                        Number(
                          this.workflowHelper.getTranslateFileTypes(
                            file.file.name,
                            FileType[file.fileFormat as keyof typeof FileType],
                          ).extension.value,
                        )
                      ];
              }
              workflowsToExecute.push(wData);
            });
          }
          //batch files from local drive
          else if (
            isNonMergeWorkflow &&
            workflowData.inputFile &&
            Array.isArray(workflowData.inputFile) &&
            workflowData.inputFile.length > 1
          ) {
            workflowData.inputFile.forEach((file) => {
              const wData = JSON.parse(JSON.stringify(workflowData));
              wData.inputFile = [file];
              wData.workflow.output.file.name = file.outputFileName;
              wData.workflow.output.file.appendDateTimeToFilename = true;
              if (wData.translateOutputFormat) {
                wData.workflow.output.file.extension =
                  wData.translateOutputFormat === 'pdf'
                    ? FileType['pdf']
                    : FileType[
                        Number(
                          this.workflowHelper.getTranslateFileTypes(
                            file.name,
                            FileType[file.fileFormat as keyof typeof FileType],
                          ).extension.value,
                        )
                      ];
              }
              workflowsToExecute.push(wData);
            });
          } else {
            workflowsToExecute.push(workflowData);
          }

          const index = 0;
          this.delayExecuteWorkflows(index, workflowsToExecute);

          // If we were hiding merge workflows, re-get them without hiding the merged workflows
          if (this.hideMergeWorkflows) {
            this.hideMergeWorkflows = false;
            this.getWorkflows();
          }
        },
        () => {
          // empty
        },
      );
    }
  }

  // execute workflows recursively
  delayExecuteWorkflows(index: number, workflows: WorkflowModalResult[]) {
    const wData = workflows[index++];
    this.workflowHelper.executeWorkflow(wData);

    if (index < workflows.length) {
      setTimeout(async () => {
        this.delayExecuteWorkflows(index, workflows);
      }, 1000);
    }
  }

  deleteWorkflow(event: MouseEvent, workflow: Workflow) {
    // stop event propagation so openWorkflow does not fire
    event.stopPropagation();
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = 'SDE_CONFIRM_DELETE_WORKFLOW';
    modalRef.componentInstance.body = workflow.name;
    modalRef.componentInstance.additional = 'SDE_IF_PUBLIC_WORKFLOW';
    modalRef.componentInstance.buttonText = 'SDE_CANCEL';
    modalRef.componentInstance.dangerButtonText = 'SDE_DELETE';
    modalRef.result.then(
      (data) => {
        if (data === 'Delete') {
          this.showActivityIndicator();

          this.workflowService.deleteWorkflow(workflow).subscribe(
            () => {
              this.showActivityIndicator(false);
              this.getWorkflows();
            },
            () => {
              this.showActivityIndicator(false);
              const modalRef = this.modalService.open(BasicAlertComponent);
              modalRef.componentInstance.title = 'SDE_ERROR_DELETING_WORKFLOW';
            },
          );
        }
      },
      () => {
        // empty
      },
    );
  }

  // refresh access list config & workflows
  configRefresh() {
    return this.accessListService
      .getAccessConfigConfig(this.accessListId, this.configUpdated)
      .then((resp) => {
        // reset configUpdated
        this.configUpdated = false;
        if (resp) {
          this.storedConfig = resp;
          this.isAutoWorkflowsDisabled =
            this.storedConfig?.disableAutoWorkflows;

          // remove disabled connectors from the cloud repos
          this.cloudRepos = [];
          Constants.middlewareDestinations.forEach((r) => {
            if (
              !this.storedConfig.disableCloudConnectors.includes(
                r.value + r.region,
              )
            ) {
              this.cloudRepos.push(r);
            }
          });
        }
      })
      .catch(() => {
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = 'SDE_CANNOT_LOAD_WORKFLOW';
        // reset configUpdated
        this.configUpdated = false;
      });
  }

  savedWorkflowsAllHidden(): boolean {
    return !this.savedWorkflows.some((w) => !w.isHidden);
  }

  openConfigOrToggle() {
    // if the config is enabled and the user has saved workflows that can be deleted expand drop down
    if (
      this.configureEnabled &&
      this.savedWorkflows?.length > 0 &&
      this.canBeDeleted
    ) {
      this.configIsExpanded = !this.configIsExpanded;
    } else {
      // else open the openConfigurationModal
      this.openConfigurationModal();
    }
  }

  openConfigurationModal() {
    const configModalRef = this.modalService.open(
      ConfigureServicesFeaturesComponent,
      { windowClass: 'xrx-modal' },
    );
    configModalRef.result.then(
      () => {
        // set configUpdated to true when openConfigurationModal closes
        this.configUpdated = true;
        this.getWorkflows().then(() => {
          this.getAutoWorkflows(this.activeTab);
        });
      },
      () => {
        // empty
      },
    );
  }

  setSortParams(param: { direction: string; column: string; type: string }) {
    this.direction = param.direction;
    this.column = param.column;
    this.type = param.type;
  }

  validateSearch(event: KeyboardEvent | FocusEvent) {
    // both keyup and blur call this function so figure out which event triggered the validation check
    if (event.type == 'keyup') {
      // for keyup we only want to validate the searchTerm length is less then the min length but greater than 0 if the enter key is hit
      if (
        (event as KeyboardEvent).keyCode == 13 &&
        this.searchTerm.length < +this.searchMinLength &&
        this.searchTerm.length > 0
      ) {
        this.searchIsValid = false;
      } else {
        this.searchIsValid = true;
      }
    } else {
      // on blur check if the searchTerm length is less then the min length but greater than 0
      if (
        this.searchTerm.length < +this.searchMinLength &&
        this.searchTerm.length > 0
      ) {
        this.searchIsValid = false;
      } else {
        this.searchIsValid = true;
      }
    }

    // show the clear search button if the searchTerm is greater then or equal to 1
    this.showClearSearch = this.searchTerm.length >= 1 ? true : false;
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchIsValid = true;
    this.showClearSearch = false;
  }

  isOverflowActive(id: string): boolean {
    const e = document.getElementById(id);
    const isOverflow =
      (e?.parentElement &&
        e?.parentElement.scrollWidth > e?.parentElement.clientWidth) ||
      (e?.parentElement &&
        e?.parentElement.scrollHeight > e?.parentElement.clientHeight)
        ? true
        : false;
    if (!isOverflow) {
      return false;
    } else if (!e?.classList.contains('line-clamp')) {
      e?.classList.add('line-clamp');
      return false;
    } else {
      return true;
    }
  }

  onToggleAutoWorkflow(workflow: AutomatedWorkflow) {
    workflow.enabled = !workflow.enabled;

    if (!workflow.enabled) {
      this.disableAutoWorkflow(workflow);
    } else {
      this.enableAutoWorkflow(workflow);
    }
  }

  disableAutoWorkflow(workflow: AutomatedWorkflow) {
    // clear access tokens
    if (workflow.input.cloudRepoOptions) {
      workflow.input.cloudRepoOptions.access = null;
    }
    if (workflow.output.cloudRepoOptions) {
      workflow.output.cloudRepoOptions.access = null;
    }

    // Load the workflows service settings to determine the workflow service type
    if (workflow.services && typeof workflow.services[0] === 'string') {
      const services: Service[] = [];
      workflow.services.forEach((x) =>
        services.push(
          this.workflowService.getSqdService(x as string, workflow.type === 1),
        ),
      );
      workflow.services = services;
    }

    this.automatedWorkflowService.saveWorkflow(workflow).subscribe(
      () => {
        // empty
      },
      () => {
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = 'SDE_ERROR_UPDATING_WORKFLOW'; 
        workflow.enabled = !workflow.enabled; // reset it back
      },
    );
  }

  enableAutoWorkflow(workflow: AutomatedWorkflow) {
    let isEditRequired = false;
    setTimeout(() => {
      workflow.enabled = false;
    }); // set toggle back to disabled until enable is successful
    this.xaccServiceLoginSub?.unsubscribe(); // unsubscribe any previous login that did not complete or was cancelled

    // re-authenticate
    if (workflow.input.cloudRepoOptions) {
      this.xaccService.showBrowser = false;
      this.xaccService.initiateWebLogin(
        {
          connector: workflow.input.cloudRepoOptions.connector,
          region: workflow.input.cloudRepoOptions.region,
        },
        true,
      );

      // wait for login to complete
      this.xaccServiceLoginSub = this.xaccService.xaccLoginSucceed.subscribe(
        async (data) => {
          this.xaccServiceLoginSub.unsubscribe();

          if (data.repo.connector === 'GraphOutlook') {
            if (workflow.emailOptions.emailAddress !== data.profile.mail) {
              workflow.emailOptions.emailAddress = data.profile.mail;

              // email address changed so invalidate output folder
              if (workflow.output.cloudRepoOptions) {
                workflow.output.cloudRepoOptions.sitePath =
                  workflow.output.cloudRepoOptions.dir = null;
                isEditRequired = true; // force user to select output folder
              }
            }
          }

          // if repo matches input repo, update the access token and validate the input path in case the user logged in with different account
          if (
            workflow.input.cloudRepoOptions?.connector.slice(0, 5) ==
            data.repo.connector.slice(0, 5)
          ) {
            try {
              if (
                workflow.input.cloudRepoOptions &&
                workflow.input.cloudRepoOptions.access
              ) {
                workflow.input.cloudRepoOptions.access = data.repo.access;
              }

              if (workflow.input.cloudRepoOptions?.dir) {
                await this.xaccService.validatePath(
                  workflow.input.cloudRepoOptions as MWCloudRepo,
                );
              }
            } catch (e) {
              if (workflow.input.cloudRepoOptions) {
                workflow.input.cloudRepoOptions.sitePath = null;
                workflow.input.cloudRepoOptions.dir = null;
              }
              console.log(e);
            } finally {
              isEditRequired =
                workflow.input.isCloudInput &&
                !workflow.input.cloudRepoOptions?.dir
                  ? true
                  : false;
            }
          }

          // if repo matches output repo, update the access token and validate the output path in case the user logged in with different account
          if (
            workflow.output.cloudRepoOptions?.connector.slice(0, 5) ==
            data.repo.connector.slice(0, 5)
          ) {
            try {
              if (workflow.output.cloudRepoOptions) {
                workflow.output.cloudRepoOptions.access = data.repo.access;
                if (workflow.output.cloudRepoOptions.dir) {
                  await this.xaccService.validatePath(
                    workflow.output.cloudRepoOptions as MWCloudRepo,
                  );
                }
              }
            } catch (e) {
              if (workflow.output.cloudRepoOptions) {
                workflow.output.cloudRepoOptions.sitePath = null;
                workflow.output.cloudRepoOptions.dir = null;
              }
              console.log(e);
            } finally {
              isEditRequired =
                !workflow.output.cloudRepoOptions?.dir || isEditRequired
                  ? true
                  : false;
            }
          }

          // Load the workflows service settings to determine the workflow service type
          if (workflow.services && typeof workflow.services[0] === 'string') {
            const services: Service[] = [];
            workflow.services.forEach((x) =>
              services.push(
                this.workflowService.getSqdService(
                  x as string,
                  workflow.type === 1,
                ),
              ),
            );
            workflow.services = services;
          }

          // Check if a Select or PDFConvert type service option is invalid
          workflow.services?.forEach((service) => {
            if ((service as Service).id !== undefined) {
              const s = service as Service;
              return (isEditRequired ||= s.serviceSettings?.find(
                (x) =>
                  ((x.type == SettingType.Select && x.required) ||
                    (x.type == SettingType.PdfConvert &&
                      JSON.parse(x.value || '{}').format == 'searchable')) &&
                  x.error?.length,
              )
                ? true
                : false);
            }
            return false;
          });

          // Check domain restrictions
          if (
            !isEditRequired &&
            this.userService.isAdvancedPlan &&
            this.accessListService.config?.enableDomainRestrictions
          ) {
            // Check output email domain is allowed
            if (
              workflow.output.outputOptions?.some((o) => o.value == 'Email')
            ) {
              isEditRequired ||= !this.accessListService.isAllowedDomain(
                workflow.output.emailAddresses || '',
              );
            }

            // If Protect workflow then validate email domains and domains list
            if (
              workflow.services &&
              typeof workflow.services[0] !== 'string' &&
              workflow.services[0]?.name === 'SDE_DOCUMENT_PROTECTION'
            ) {
              if (
                workflow.services &&
                typeof workflow.services[0] !== 'string' &&
                workflow.services[0].serviceSettings
              ) {
                const settings: ActiveDocType = JSON.parse(
                  workflow.services[0].serviceSettings[0].value || '',
                );

                if (settings.documentOptions.recipients?.length) {
                  isEditRequired ||= settings.documentOptions.recipients.some(
                    (recipient) =>
                      this.accessListService.isAllowedDomain(recipient.email) ==
                      false,
                  );
                }
                if (
                  settings.securityMode?.label === 'SDE_TRACK_VIEWERS' &&
                  settings.documentOptions.domains?.length
                ) {
                  const domains = settings.documentOptions.domains.split(',');
                  isEditRequired ||= domains.some(
                    (domain) =>
                      this.accessListService.isAllowedDomain(domain) == false,
                  );
                }
              }
            }
          }

          workflow.enabled = true;

          this.automatedWorkflowService.saveWorkflow(workflow).subscribe(
            () => {
              if (isEditRequired) {
                this.editAutoWorkflow(workflow);
              }
            },
            (error) => {
              console.log(error);
              const modalRef = this.modalService.open(BasicAlertComponent);
              modalRef.componentInstance.title = 'SDE_ERROR_UPDATING_WORKFLOW'; 
            },
          );
        },
      );
    }
  }

  viewAutoWorkflowHistory(event: MouseEvent, workflow: AutomatedWorkflow) {
    event.stopPropagation(); // stop click from opening tooltip

    const jobHistoryModal = this.modalService.open(
      XrxJobHistoryModalComponent,
      { windowClass: 'xrx-modal', centered: true, backdrop: 'static' },
    );
    jobHistoryModal.componentInstance.workflow = workflow;

    jobHistoryModal.componentInstance.refreshJobs.subscribe(
      (jobs: AutomatedJobHistory[]) => {
        let succeeded = 0,
          failed = 0;
        jobs.forEach((job) => {
          switch (job.status[0].state) {
            case SqdStatusState.Completed:
              succeeded++;
              break;
            case SqdStatusState.Errored:
              failed++;
              break;
          }
        });
        const wf = this.automatedWorkflows.find((x) => x.id == workflow.id);
        if (wf) {
          wf.jobStatus = { completed: succeeded, failed: failed };
          wf.jobs = succeeded + failed;
        }
      },
    );
  }

  editAutoWorkflow(workflow: AutomatedWorkflow) {
    this.addAutoWorkflow(workflow);
  }

  async addAutoWorkflow(workflow: AutomatedWorkflow | null = null) {
    this.xaccServiceLoginSub?.unsubscribe(); // unsubscribe any logins that didn't complete or was cancelled
    await this.accessListService.refreshAccessConfigConfig(
      this.userService.user?.accessListId,
    ); // refresh stale config data

    const modalRef = this.modalService.open(AutomatedWorkflowModalComponent, {
      windowClass: 'xrx-modal',
      modalDialogClass: 'xrx-automated-modal',
      size: 'lg',
      centered: true,
      backdrop: 'static',
    });
    const autoWorkflows = [
      'SDE_CONVERT_AUDIO',
      'SDE_CONVERT_MS_OFFICE',
      'SDE_CONVERT_TO_PDF1',
      'SDE_HANDWRITING_TEXT',
      'SDE_DRM',
      'SDE_REDACT',
      'SDE_SEND_FAX',
      'SDE_EXTRACTIVE_SUMMARY',
      'SDE_TRANSLATE',
    ];
    const unsupportedRepos = ['Dropbox', 'Box', 'DocuShare'];
    if (this.xdaStringsService.strings?.['ENABLEGOOGLEDRIVE'] !== 'true') {
      unsupportedRepos.push('GoogleDrive');
    }
    modalRef.componentInstance.workflows = this.preconfiguredWorkflows.filter(
      (item) => autoWorkflows.includes(item.name),
    );
    modalRef.componentInstance.cloudRepos = this.cloudRepos.filter(
      (repo) => !unsupportedRepos.includes(repo.value),
    );
    if (workflow) {
      modalRef.componentInstance.workflow = workflow; // edit mode
    }
    modalRef.componentInstance.update.subscribe(() => {
      this.getAutoWorkflows(this.activeTab);
      this.workflowService.loadSqdServices(); // is this needed?
    });
  }

  deleteAutoWorkflow(event: MouseEvent, workflow: AutomatedWorkflow) {
    // stop event propagation
    event.stopPropagation();
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = 'SDE_CONFIRM_DELETE_AUTOMATED';
    modalRef.componentInstance.body = workflow.name;
    modalRef.componentInstance.buttonText = 'SDE_CANCEL';
    modalRef.componentInstance.dangerButtonText = 'SDE_DELETE';
    modalRef.result.then(
      (data) => {
        if (data === 'Delete') {
          this.showActivityIndicator();

          this.automatedWorkflowService
            .deleteWorkflow(workflow as AutomatedWorkflow)
            .subscribe(
              () => {
                this.showActivityIndicator(false);
                // Calculate if current page will be empty after deletion
                const remainingItems = this.automatedWorkflows.length - 1;
                const maxPage = Math.ceil(remainingItems / this.pageSize);
                // Reset to previous page only if current page will be empty
                if (this.page > maxPage && maxPage > 0) {
                  this.page = maxPage;
                }
                this.getAutoWorkflows(this.activeTab);
              },
              () => {
                this.showActivityIndicator(false);
                const modalRef = this.modalService.open(BasicAlertComponent);
                modalRef.componentInstance.title =
                  'SDE_ERROR_DELETING_WORKFLOW';
              },
            );
        }
      },
      () => {
        // empty
      },
    );
  }

  runNow(event: MouseEvent, workflow: AutomatedWorkflow) {
    event.stopPropagation(); // stop click from opening tooltip

    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = workflow.input.isCloudInput
      ? 'SDE_MAKE_SURE_HAVE1'
      : 'SDE_MAKE_SURE_HAVE';
    modalRef.componentInstance.extraClasses = 'run-now-alert';
    modalRef.componentInstance.body = workflow.name;
    modalRef.componentInstance.buttonText = 'SDE_CANCEL';
    modalRef.componentInstance.okButtonText = 'SDE_CONTINUE';
    modalRef.result.then((data) => {
      if (data === 'Ok') {
        this.toggleRunningWorkflow = true;
        this.runningWorkflowName = workflow.name;

        // execute the workflow now...
        this.automatedWorkflowService
          .runWorkflowNow(this.accessListId, workflow)
          .subscribe(
            () => {
              // empty
            },
            () => {
              //TODO Error handling?
            },
          );
        setTimeout(() => {
          this.toggleRunningWorkflow = false;
        }, 3000); // dismiss after 3 seconds
      }
    });
  }

  openWorkflowTooltip(index: number) {
    setTimeout(() => {
      if (!this.wfTooltips[index]?.isOpen()) {
        this.wfTooltips[index]?.open();
        setTimeout(() => {
          this.wfTooltips[index]?.close();
        }, 5000);
      }
    }, 250);
  }

  closeWorkflowTooltip(event: Event, index: number) {
    // close workflow 'disabled' tooltip if hover over the enabled delete or history icons that display their own tooltip
    const target = event.target as Element;
    if (
      this.wfTooltips[index]?.isOpen() &&
      (target.classList.contains('wf-icon-delete') ||
        target.classList.contains('wf-icon-history'))
    ) {
      this.wfTooltips[index]?.close();
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Make run now alert sticky if scrolled off screen
    if (this.toggleRunningWorkflow) {
      const brandHeaderHeight = 64; // 4rem
      const alertHeight = 143;
      if (window.pageYOffset > brandHeaderHeight + alertHeight) {
        this.isSticky = true;
      } else if (window.pageYOffset < brandHeaderHeight) {
        this.isSticky = false;
      }
    } else {
      this.isSticky = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickGlobal(event: MouseEvent) {
    // If dropdown is expanded, and click event comes from outside this element, close dropdown
    if (
      this.configIsExpanded &&
      !this.configDropdown.nativeElement.contains(event.target)
    )
      this.configIsExpanded = false;
  }

  ngOnDestroy(): void {
    this.accessListDiscoveredSub.unsubscribe();
    this.xaccServiceLoginSub?.unsubscribe();
  }

  navChanged(event: NgbNavChangeEvent) {
    if (event.nextId === 'SDE_AUTOMATED_WORKFLOWS') {
      this.getAutoWorkflows(event.nextId);
    }
  }
}
